import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SubmissionStatusTimeline } from "@/components/SubmissionStatusTimeline";
import { toast } from "sonner";
import { User, Dribbble, Award, Save, TrendingUp, Home, Heart, Loader2, DollarSign, MapPin, Shield, Dumbbell, GraduationCap, Plus, Star, Briefcase, Building2, CheckCircle, Upload, CreditCard, FileText, Lock, Eye, EyeOff, RotateCcw, Mail, X, Clock } from "lucide-react";
import { ALL_SPORTS, SPORT_CATEGORIES } from "@/lib/sports";

const SKILL_LEVELS = [
  { value: "beginner", label: "Beginner", desc: "Just starting out", color: "bg-blue-100 text-blue-700" },
  { value: "intermediate", label: "Intermediate", desc: "Play regularly", color: "bg-green-100 text-green-700" },
  { value: "advanced", label: "Advanced", desc: "Compete locally", color: "bg-amber-100 text-amber-700" },
  { value: "competitive", label: "Competitive", desc: "Tournament-level", color: "bg-purple-100 text-purple-700" },
];
const COACHING_LEVELS = ["beginner","intermediate","advanced","elite","national"];
const TRAINING_TYPES = ["strength","conditioning","rehabilitation","sports_specific","functional","group_fitness"];
const OFFICIAL_TYPES = ["referee","umpire","judge","line_official","table_official"];

function ChangePasswordCard() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async () => {
    if (!currentPw) return toast.error("Enter your current password");
    if (newPw.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("Passwords don't match");
    if (currentPw === newPw) return toast.error("New password must be different");
    setSaving(true);
    try {
      await api.put("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed successfully");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setSaving(false);
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Lock className="w-5 h-5" /> Change Password</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Current Password</Label>
          <div className="relative">
            <Input data-testid="current-password-input" type={showCurrent ? "text" : "password"} value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Enter current password" />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowCurrent(!showCurrent)}>
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label>New Password</Label>
          <div className="relative">
            <Input data-testid="new-password-input" type={showNew ? "text" : "password"} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 6 characters" />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowNew(!showNew)}>
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label>Confirm New Password</Label>
          <Input data-testid="confirm-password-input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Re-enter new password" />
          {confirmPw && newPw !== confirmPw && <p className="text-xs text-red-500 mt-1">Passwords don't match</p>}
        </div>
        <Button data-testid="change-password-btn" onClick={handleChange} disabled={saving || !currentPw || !newPw || newPw !== confirmPw}
          className="bg-court-orange hover:bg-court-orange-hover text-white font-heading uppercase">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
          Change Password
        </Button>
      </CardContent>
    </Card>
  );
}

function ChangeEmailCard({ currentEmail }) {
  const [step, setStep] = useState("idle"); // idle, enter_email, verify_otp, pending_admin
  const [newEmail, setNewEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingReq, setPendingReq] = useState(null);

  useEffect(() => {
    api.get("/auth/email-change-status").then(r => {
      if (r.data.has_pending) {
        setPendingReq(r.data);
        setStep(r.data.status === "pending_otp" ? "verify_otp" : "pending_admin");
        setNewEmail(r.data.new_email || "");
      }
    }).catch(() => {});
  }, []);

  const requestChange = async () => {
    if (!newEmail || !newEmail.includes("@")) return toast.error("Enter a valid email");
    if (newEmail.toLowerCase() === currentEmail?.toLowerCase()) return toast.error("New email must be different");
    setSaving(true);
    try {
      await api.post("/auth/request-email-change", { new_email: newEmail });
      toast.success("Verification code sent to your new email!");
      setStep("verify_otp");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send verification"); }
    setSaving(false);
  };

  const verifyOtp = async () => {
    if (!otp || otp.length < 6) return toast.error("Enter the 6-digit code");
    setSaving(true);
    try {
      await api.post("/auth/verify-email-change-otp", { otp_code: otp });
      toast.success("Email verified! Awaiting admin approval.");
      setStep("pending_admin");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    setSaving(false);
  };

  const cancelRequest = async () => {
    setSaving(true);
    try {
      await api.post("/auth/cancel-email-change");
      toast.success("Email change request cancelled");
      setStep("idle"); setNewEmail(""); setOtp(""); setPendingReq(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to cancel"); }
    setSaving(false);
  };

  return (
    <Card data-testid="change-email-card">
      <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Mail className="w-5 h-5" /> Change Email</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">Current: <span className="font-medium text-foreground">{currentEmail}</span></p>

        {step === "idle" && (
          <>
            <div>
              <Label>New Email Address</Label>
              <Input data-testid="new-email-input" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Enter new email address" />
            </div>
            <Button data-testid="request-email-change-btn" onClick={requestChange} disabled={saving || !newEmail}
              className="bg-court-orange hover:bg-court-orange-hover text-white font-heading uppercase">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Verification Code
            </Button>
          </>
        )}

        {step === "verify_otp" && (
          <>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800">A 6-digit verification code has been sent to <span className="font-bold">{newEmail}</span></p>
            </div>
            <div>
              <Label>Verification Code</Label>
              <Input data-testid="email-otp-input" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter 6-digit code" maxLength={6} className="text-center text-xl tracking-[0.5em] font-mono" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="verify-email-otp-btn" onClick={verifyOtp} disabled={saving || otp.length < 6}
                className="flex-1 bg-court-orange hover:bg-court-orange-hover text-white font-heading uppercase">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Verify
              </Button>
              <Button variant="outline" onClick={cancelRequest} disabled={saving} className="font-heading uppercase">
                <X className="w-4 h-4 mr-1" />Cancel
              </Button>
            </div>
          </>
        )}

        {step === "pending_admin" && (
          <div className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2">
              <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Awaiting Admin Approval</p>
                <p className="text-xs text-amber-700">Your request to change email to <span className="font-bold">{newEmail}</span> has been verified and is pending admin review.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={cancelRequest} disabled={saving} className="text-xs font-heading uppercase">
              <X className="w-3 h-3 mr-1" />Cancel Request
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProfileSettings() {
  const { user, checkAuth } = useAuth();
  const [venues, setVenues] = useState([]);
  const [venueSearch, setVenueSearch] = useState("");
  const [tab, setTab] = useState("personal"); // Default, will be updated based on role
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", city_id: "", sports: [], sport_skills: {}, home_court_id: "", favorite_venue_id: "" });
  const [spProfile, setSpProfile] = useState(null);
  const [spForm, setSpForm] = useState({
    bio: "", years_experience: 0, service_locations: "",
    governing_body: "", insurance_declared: false,
    coaching_level: "", specializations: "", max_group_size: 15,
    training_type: "", mobile_service: false, equipment_required: "",
    official_type: "", certification_level: "",
    social_facebook: "", social_instagram: "", social_tiktok: "",
    per_hour: 0, per_game: 0, per_session: 0, half_day: 0, full_day: 0,
    series_discount_pct: 10, travel_surcharge: 0, peak_multiplier: 1.5,
  });
  const [saving, setSaving] = useState(false);
  const [personaData, setPersonaData] = useState({ personas: [], pending_requests: [] });
  const [personaRequestType, setPersonaRequestType] = useState("");
  const [personaRequestMessage, setPersonaRequestMessage] = useState("");
  const [requestingPersona, setRequestingPersona] = useState(false);

  const isSP = user?.is_coach || user?.is_sp;
  const isProvider = user?.role === "PROVIDER";
  
  // Set default tab based on user role
  useEffect(() => {
    if (isProvider) {
      setTab("venue-owner");
    } else if (isSP) {
      setTab("professional");
    } else {
      setTab("personal");
    }
  }, [isProvider, isSP]);

  // Venue Owner KYC state
  const [kyc, setKyc] = useState(null);
  const [kycForm, setKycForm] = useState({
    venue_role: "Venue Owner", gov_id_type: "", payout_method: "bank_account",
    bank_name: "", account_number: "", account_name: "",
    gcash_number: "", maya_number: "",
  });
  const [govIdFile, setGovIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [identitySubmitted, setIdentitySubmitted] = useState(false);
  const [editIdentityWarning, setEditIdentityWarning] = useState(false);

  // Player KYC state
  const [playerKyc, setPlayerKyc] = useState(null);
  const [playerKycForm, setPlayerKycForm] = useState({
    birthday: "", gov_id_type: "", emergency_contact: "",
    secondary_phone: "", social_facebook: "", social_instagram: "",
  });
  const [playerGovIdFile, setPlayerGovIdFile] = useState(null);
  const [playerSelfieFile, setPlayerSelfieFile] = useState(null);
  const [playerUploading, setPlayerUploading] = useState(false);
  const [playerAgeError, setPlayerAgeError] = useState("");
  const [playerRetracting, setPlayerRetracting] = useState(false);

  // SP KYC state
  const [spKyc, setSpKyc] = useState(null);
  const [spKycForm, setSpKycForm] = useState({
    gov_id_type: "", credential_type: "", credential_ref1: "", credential_ref2: "",
    payout_method: "gcash", bank_name: "", account_number: "", account_name: "",
    gcash_number: "", maya_number: "",
  });
  // SP KYC document upload state
  const [spGovIdFile, setSpGovIdFile] = useState(null);
  const [spSelfieFile, setSpSelfieFile] = useState(null);
  const [spCredentialFiles, setSpCredentialFiles] = useState([]); // Array of credential docs
  const [spUploading, setSpUploading] = useState(false);
  const [spIdentitySubmitted, setSpIdentitySubmitted] = useState(false);

  useEffect(() => {
    api.get("/venues").then(r => setVenues(r.data || [])).catch(() => {});
    if (user) {
      setForm({ name: user.name || "", phone: user.phone || "", city_id: user.city_id || "",
        sports: user.sports || [], sport_skills: user.sport_skills || {},
        home_court_id: user.home_court_id || "", favorite_venue_id: user.favorite_court_ids?.[0] || "" });
      api.get("/auth/personas/mine").then(r => setPersonaData(r.data)).catch(() => {});
    }
    if (isProvider) {
      api.get("/venue-wizard/kyc-status").then(r => {
        setKyc(r.data);
        setKycForm(prev => ({
          ...prev,
          venue_role: r.data.venue_role || user?.venue_role || "Venue Owner",
          gov_id_type: r.data.gov_id_type || "",
          payout_method: r.data.payout_method || "bank_account",
          bank_name: r.data.payout_bank_name || "",
          account_number: r.data.payout_account_number || "",
          account_name: r.data.payout_account_name || "",
          gcash_number: r.data.payout_gcash_number || "",
          maya_number: r.data.payout_maya_number || "",
        }));
      }).catch(() => {});
    }
    // Fetch player KYC
    if (!isProvider) {
      api.get("/player/kyc-status").then(r => {
        setPlayerKyc(r.data);
        setPlayerKycForm(prev => ({
          ...prev,
          birthday: r.data.birthday || user?.birthday || "",
          gov_id_type: r.data.gov_id_type || "",
          emergency_contact: r.data.emergency_contact || "",
          secondary_phone: r.data.secondary_phone || "",
          social_facebook: r.data.social_facebook || "",
          social_instagram: r.data.social_instagram || "",
        }));
      }).catch(() => {});
    }
    if (isSP) {
      api.get("/sp/profile").then(r => {
        const sp = r.data;
        setSpProfile(sp);
        const ra = sp.role_attributes || {};
        const p = sp.pricing || {};
        setSpForm({
          bio: sp.bio || "", years_experience: sp.years_experience || 0,
          service_locations: (sp.service_locations || []).join(", "),
          governing_body: sp.governing_body || "", insurance_declared: sp.insurance_declared || false,
          coaching_level: ra.coach?.coaching_level || "", specializations: (ra.coach?.specializations || []).join(", "),
          max_group_size: ra.coach?.max_group_size || 15,
          training_type: ra.trainer?.training_type || "", mobile_service: ra.trainer?.mobile_service || false,
          equipment_required: ra.trainer?.equipment_required || "",
          official_type: ra.official?.official_type || "", certification_level: ra.official?.certification_level || "",
          social_facebook: sp.social_links?.facebook || "", social_instagram: sp.social_links?.instagram || "", social_tiktok: sp.social_links?.tiktok || "",
          per_hour: p.per_hour || 0, per_game: p.per_game || 0, per_session: p.per_session || 0,
          half_day: p.half_day || 0, full_day: p.full_day || 0,
          series_discount_pct: p.series_discount_pct || 10, travel_surcharge: p.travel_surcharge || 0,
          peak_multiplier: p.peak_multiplier || 1.5,
        });
      }).catch(() => {});
      api.get("/sp/kyc-status").then(r => {
        setSpKyc(r.data);
        setSpKycForm(prev => ({
          ...prev,
          gov_id_type: r.data.gov_id_type || "",
          payout_method: r.data.payout_method || "gcash",
        }));
      }).catch(() => {});
    }
  }, [user, isSP]);

  const toggleSport = (sport) => {
    setForm(prev => {
      const ns = prev.sports.includes(sport) ? prev.sports.filter(s => s !== sport) : [...prev.sports, sport];
      const nsk = { ...prev.sport_skills };
      if (!ns.includes(sport)) delete nsk[sport]; else if (!nsk[sport]) nsk[sport] = "beginner";
      return { ...prev, sports: ns, sport_skills: nsk };
    });
  };

  const setSkill = (sport, level) => setForm(prev => ({ ...prev, sport_skills: { ...prev.sport_skills, [sport]: level } }));

  const handleSavePersonal = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", { name: form.name, phone: form.phone, city_id: form.city_id, sports: form.sports, sport_skills: form.sport_skills });
      if (form.home_court_id) try { await api.put("/player/home-court", { venue_id: form.home_court_id }); } catch {}
      if (form.favorite_venue_id) try { await api.post("/player/favorite-court", { venue_id: form.favorite_venue_id, action: "add" }); } catch {}
      await checkAuth(); toast.success("Profile updated!");
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    setSaving(false);
  };

  const handleSaveProfessional = async () => {
    setSaving(true);
    try {
      // Update SP profile
      await api.put("/sp/profile", {
        bio: spForm.bio, years_experience: spForm.years_experience,
        service_locations: spForm.service_locations.split(",").map(s => s.trim()).filter(Boolean),
        governing_body: spForm.governing_body, insurance_declared: spForm.insurance_declared,
        social_links: { facebook: spForm.social_facebook, instagram: spForm.social_instagram, tiktok: spForm.social_tiktok },
        role_attributes: {
          ...(spProfile?.roles_enabled?.includes("coach") ? { coach: { coaching_level: spForm.coaching_level, specializations: spForm.specializations.split(",").map(s => s.trim()).filter(Boolean), max_group_size: spForm.max_group_size } } : {}),
          ...(spProfile?.roles_enabled?.includes("trainer") ? { trainer: { training_type: spForm.training_type, mobile_service: spForm.mobile_service, equipment_required: spForm.equipment_required } } : {}),
          ...(spProfile?.roles_enabled?.includes("official") ? { official: { official_type: spForm.official_type, certification_level: spForm.certification_level } } : {}),
        },
      });
      // Update pricing
      const pricing = {};
      if (spForm.per_hour) pricing.per_hour = parseFloat(spForm.per_hour);
      if (spForm.per_game) pricing.per_game = parseFloat(spForm.per_game);
      if (spForm.per_session) pricing.per_session = parseFloat(spForm.per_session);
      if (spForm.half_day) pricing.half_day = parseFloat(spForm.half_day);
      if (spForm.full_day) pricing.full_day = parseFloat(spForm.full_day);
      pricing.series_discount_pct = parseFloat(spForm.series_discount_pct) || 10;
      pricing.travel_surcharge = parseFloat(spForm.travel_surcharge) || 0;
      pricing.peak_multiplier = parseFloat(spForm.peak_multiplier) || 1.5;
      await api.put("/sp/pricing", pricing);
      toast.success("Professional profile updated!");
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    setSaving(false);
  };

  const handleSaveVenueOwner = async () => {
    setSaving(true);
    try {
      let govIdUrl = kyc?.gov_id_url || "";
      let selfieUrl = kyc?.selfie_url || "";
      
      // Upload files if provided
      if (govIdFile || selfieFile) {
        setUploading(true);
        
        // Upload Government ID
        if (govIdFile) {
          const formData = new FormData();
          formData.append("file", govIdFile);
          formData.append("doc_type", "government_id");
          formData.append("category", "kyc_identity");
          const uploadRes = await api.post("/uploads/kyc-document", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          govIdUrl = uploadRes.data.url;
        }
        
        // Upload Selfie
        if (selfieFile) {
          const formData = new FormData();
          formData.append("file", selfieFile);
          formData.append("doc_type", "selfie");
          formData.append("category", "kyc_identity");
          const uploadRes = await api.post("/uploads/kyc-document", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          selfieUrl = uploadRes.data.url;
        }
        setUploading(false);
      }
      
      // Save identity with document URLs
      if (kycForm.gov_id_type) {
        await api.post("/venue-wizard/kyc/identity", { 
          venue_role: kycForm.venue_role, 
          gov_id_type: kycForm.gov_id_type,
          gov_id_url: govIdUrl,
          selfie_url: selfieUrl,
        });
      }
      // Save payment
      await api.post("/venue-wizard/kyc/payment", {
        payout_method: kycForm.payout_method,
        bank_name: kycForm.bank_name, account_number: kycForm.account_number, account_name: kycForm.account_name,
        gcash_number: kycForm.gcash_number, maya_number: kycForm.maya_number,
      });
      // Refresh
      const res = await api.get("/venue-wizard/kyc-status");
      setKyc(res.data);
      setIdentitySubmitted(true);
      toast.success("Venue owner profile saved! Your identity documents are pending review.");
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    setSaving(false);
  };

  const handlePersonaRequest = async () => {
    if (!personaRequestType) {
      toast.error("Choose a persona to apply for");
      return;
    }
    setRequestingPersona(true);
    try {
      const res = await api.post("/auth/personas/request", {
        persona_type: personaRequestType,
        message: personaRequestMessage,
      });
      toast.success(`${res.data.persona_label} request submitted.`);
      setPersonaRequestType("");
      setPersonaRequestMessage("");
      const refresh = await api.get("/auth/personas/mine");
      setPersonaData(refresh.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to submit request");
    } finally {
      setRequestingPersona(false);
    }
  };

  const handleSubmitPlayerVerification = async () => {
    if (playerAgeError) {
      toast.error(playerAgeError);
      return;
    }
    setPlayerUploading(true);
    try {
      let govIdUrl = playerKyc?.gov_id_url || "";
      let selfieUrl = playerKyc?.selfie_url || "";

      if (playerGovIdFile) {
        const formData = new FormData();
        formData.append("file", playerGovIdFile);
        formData.append("doc_type", "government_id");
        formData.append("category", "player_kyc_identity");
        const uploadRes = await api.post("/uploads/kyc-document", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        govIdUrl = uploadRes.data.url;
      }

      if (playerSelfieFile) {
        const formData = new FormData();
        formData.append("file", playerSelfieFile);
        formData.append("doc_type", "selfie");
        formData.append("category", "player_kyc_identity");
        const uploadRes = await api.post("/uploads/kyc-document", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        selfieUrl = uploadRes.data.url;
      }

      const submitRes = await api.post("/player/kyc/verify-identity", {
        gov_id_type: playerKycForm.gov_id_type,
        birthday: playerKycForm.birthday,
        gov_id_url: govIdUrl,
        selfie_url: selfieUrl,
      });

      toast.success(submitRes.data.status || "Identity documents submitted for review.");
      setPlayerGovIdFile(null);
      setPlayerSelfieFile(null);
      const res = await api.get("/player/kyc-status");
      setPlayerKyc(res.data);
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setPlayerUploading(false);
    }
  };

  const handleRetractPlayerKyc = async () => {
    setPlayerRetracting(true);
    try {
      await api.post("/player/kyc/retract");
      toast.success("KYC submission retracted. You may now edit and resubmit.");
      const res = await api.get("/player/kyc-status");
      setPlayerKyc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retract failed");
    }
    setPlayerRetracting(false);
  };

  const currentPersonaKeys = (personaData.personas?.length
    ? personaData.personas.map((persona) => typeof persona === "string" ? persona : persona.persona_type)
    : (user?.personas || []));
  const pendingPersonaRequests = (personaData.pending_requests || []).filter((req) => req.status === "pending");

  const validateAdultBirthday = (value) => {
    if (!value) return "";
    const today = new Date();
    const birth = new Date(value);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age < 18 ? "You must be at least 18 years old to submit this profile for verification." : "";
  };

  const deletePlayerDoc = async (docType) => {
    try {
      await api.delete(`/uploads/kyc-document/${docType}`);
      toast.success("Document removed. You can upload a new one now.");
      const res = await api.get("/player/kyc-status");
      setPlayerKyc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove document");
    }
  };

  const deleteSpDoc = async (docType) => {
    try {
      await api.delete(`/uploads/kyc-document/${docType}`);
      toast.success("Document removed. You can upload a new one now.");
      const res = await api.get("/sp/kyc-status");
      setSpKyc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove document");
    }
  };

  const playerChecklist = [];
  if (playerKyc?.identity_review_notes) playerChecklist.push(`Admin note: ${playerKyc.identity_review_notes}`);
  if (!playerKycForm.birthday) playerChecklist.push("Complete your birthday field");
  if (!playerKyc?.gov_id_url) playerChecklist.push("Upload a valid government ID");
  if (!playerKyc?.selfie_url) playerChecklist.push("Upload a clear selfie holding your ID");

  const spChecklist = [];
  if (spKyc?.identity_review_notes) spChecklist.push(`Admin note: ${spKyc.identity_review_notes}`);
  if (spKyc?.credentials_review_notes) spChecklist.push(`Credential review note: ${spKyc.credentials_review_notes}`);
  if (!spKyc?.gov_id_url) spChecklist.push("Upload a valid government ID");
  if (!spKyc?.selfie_url) spChecklist.push("Upload a clear selfie holding your ID");
  if (!(spKyc?.credential_urls || []).length) spChecklist.push("Upload your professional credential documents");

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6" data-testid="profile-settings-page">
      <h1 className="font-heading text-2xl sm:text-3xl">Profile Settings</h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`grid w-full ${isSP && isProvider ? "grid-cols-3" : isSP || isProvider ? "grid-cols-2" : "grid-cols-1"}`}>
          <TabsTrigger value="personal" className="flex items-center gap-1"><User className="w-4 h-4" />Personal</TabsTrigger>
          {isProvider && <TabsTrigger value="venue-owner" className="flex items-center gap-1"><Building2 className="w-4 h-4" />Venue Owner</TabsTrigger>}
          {isSP && <TabsTrigger value="professional" className="flex items-center gap-1"><Briefcase className="w-4 h-4" />Professional</TabsTrigger>}
        </TabsList>

        {/* ═══ PERSONAL TAB ═══ */}
        <TabsContent value="personal" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><User className="w-5 h-5" /> Personal Info</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Full Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="profile-name-input" /></div>
              <div>
                <Label>Email</Label>
                <div className="flex items-center gap-2">
                  <Input value={user?.email || ""} readOnly className="bg-muted/50" data-testid="profile-email" />
                  {user?.email && !user?.password_hash && (
                    <Badge className="bg-blue-100 text-blue-700 text-[9px] shrink-0"><svg viewBox="0 0 24 24" className="w-3 h-3 mr-0.5 inline" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>Google</Badge>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{user?.email && !user?.password_hash ? "This email is linked to your Google social login" : "Email used for login and notifications"}</p>
              </div>
              <div><Label>Phone Number</Label><Input value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+63 9XX XXX XXXX" /></div>
            </CardContent>
          </Card>

          <ChangePasswordCard />
          <ChangeEmailCard currentEmail={user?.email} />

          <Card data-testid="persona-application-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Star className="w-5 h-5" /> Persona Access
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Your active personas</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {currentPersonaKeys.map((persona) => (
                    <Badge key={persona} variant="outline" data-testid={`persona-badge-${persona.toLowerCase()}`}>
                      {persona === "PLAYER" ? "Player" : persona === "VENUE_OWNER" ? "Venue Owner" : persona === "COACH_SP" ? "Coach / Trainer / SP" : persona}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Apply for another persona</Label>
                <Select value={personaRequestType} onValueChange={setPersonaRequestType}>
                  <SelectTrigger data-testid="persona-request-select" className="mt-2">
                    <SelectValue placeholder="Choose a persona to apply for" />
                  </SelectTrigger>
                  <SelectContent>
                    {!currentPersonaKeys.includes("PLAYER") && <SelectItem value="PLAYER">Player</SelectItem>}
                    {!currentPersonaKeys.includes("VENUE_OWNER") && <SelectItem value="VENUE_OWNER">Venue Owner</SelectItem>}
                    {!currentPersonaKeys.includes("COACH_SP") && <SelectItem value="COACH_SP">Coach / Trainer / Sports Professional</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-heading uppercase tracking-wider">Message to admin (optional)</Label>
                <Textarea value={personaRequestMessage} onChange={e => setPersonaRequestMessage(e.target.value)} placeholder="Tell the admin why you're requesting this persona." data-testid="persona-request-message" />
              </div>

              <Button onClick={handlePersonaRequest} disabled={requestingPersona || !personaRequestType} className="rounded-full bg-court-orange hover:bg-court-orange-hover text-white" data-testid="submit-persona-request-btn">
                {requestingPersona ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Submit Persona Request
              </Button>

              {pendingPersonaRequests.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="pending-persona-requests">
                  <p className="text-xs font-heading uppercase tracking-wider text-amber-800 mb-2">Pending requests</p>
                  <div className="space-y-1">
                    {pendingPersonaRequests.map((req) => (
                      <p key={req.request_id} className="text-sm text-amber-900">
                        {req.persona_label} — under processing
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Home className="w-5 h-5" /> My Home Court / Venue</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><Label className="flex items-center gap-1"><Home className="w-3 h-3 text-primary" /> My Home Court</Label>
                <Select value={form.home_court_id} onValueChange={v => setForm({...form, home_court_id: v})}><SelectTrigger><SelectValue placeholder="Select home court..." /></SelectTrigger>
                  <SelectContent className="max-h-60">{venues.filter(v => !venueSearch || v.name.toLowerCase().includes(venueSearch.toLowerCase())).map(v => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name} — {v.city}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-500" /> My Favorite Venue</Label>
                <Select value={form.favorite_venue_id} onValueChange={v => setForm({...form, favorite_venue_id: v})}><SelectTrigger><SelectValue placeholder="Select favorite..." /></SelectTrigger>
                  <SelectContent className="max-h-60">{venues.map(v => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name} — {v.city}</SelectItem>)}</SelectContent>
                </Select></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Dribbble className="w-5 h-5" /> Sports Preferences</CardTitle></CardHeader>
            <CardContent>
              {SPORT_CATEGORIES.map(cat => (
                <div key={cat.key} className="mb-3"><p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-1.5">{cat.label}</p>
                  <div className="flex flex-wrap gap-2">{ALL_SPORTS.filter(s => s.category === cat.key).map(s => <Badge key={s.value} variant={form.sports.includes(s.value) ? "default" : "outline"} className="cursor-pointer" onClick={() => toggleSport(s.value)}>{s.label}</Badge>)}</div>
                </div>))}
            </CardContent>
          </Card>

          {form.sports.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Skill Assessment</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {form.sports.map(sport => {
                  const current = form.sport_skills[sport] || "beginner";
                  const info = SKILL_LEVELS.find(l => l.value === current);
                  return (<div key={sport} className="border rounded-lg p-4 space-y-3"><div className="flex items-center justify-between"><span className="font-heading text-sm uppercase capitalize">{sport.replace("-"," ")}</span><Badge className={info?.color}>{info?.label}</Badge></div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{SKILL_LEVELS.map(l => <button key={l.value} onClick={() => setSkill(sport, l.value)} className={`p-2 rounded-lg border-2 text-center ${current === l.value ? "border-primary bg-primary/5" : "border-slate-200"}`}><p className="text-xs font-bold truncate">{l.label}</p><p className="text-[9px] text-muted-foreground truncate">{l.desc}</p></button>)}</div></div>);
                })}
              </CardContent>
            </Card>
          )}

          {(user?.badges?.length > 0 || user?.founding_flag || user?.ambassador_flag) && (
            <Card><CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Award className="w-5 h-5" /> Badges</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {user?.founding_flag && <Badge className="bg-amber-500 text-white">Founding Player</Badge>}
                {user?.ambassador_flag && <Badge className="bg-teal-600 text-white">Ambassador</Badge>}
                {(user?.badges || []).filter(b => !["founding_player","ambassador"].includes(b)).map(b => <Badge key={b} variant="secondary" className="capitalize">{b.replace("_"," ")}</Badge>)}
              </CardContent></Card>
          )}

          {/* Player Account Verification */}
          {!isProvider && playerKyc && (
            <Card className="border-primary/20">
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Shield className="w-5 h-5 text-primary" /> Account Verification</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Level Progress */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Verification Level</span>
                  <Badge className={playerKyc.kyc_level >= 3 ? "bg-green-100 text-green-800" : playerKyc.kyc_level >= 2 ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}>
                    Level {playerKyc.kyc_level}: {playerKyc.levels?.[playerKyc.kyc_level]?.label || "Basic"}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[{lvl:1,label:"Basic Account",desc:"OTP verified"},{lvl:2,label:"Verified Player",desc:"Gov ID checked"},{lvl:3,label:"Trusted Player",desc:"10+ bookings, 0 disputes"}].map(s => (
                    <div key={s.lvl} className={`text-center p-3 rounded-lg border ${playerKyc.kyc_level >= s.lvl ? "bg-green-50 border-green-200" : "border-dashed"}`}>
                      <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${playerKyc.kyc_level >= s.lvl ? "text-green-500" : "text-slate-200"}`} />
                      <p className="text-[10px] font-heading font-bold">{s.label}</p>
                      <p className="text-[9px] text-muted-foreground">{s.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Level 2: ID Verification */}
                {playerKyc.kyc_level < 2 && (
                  <>
                    <Separator />
                    <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Verify Your Account</p>
                    <p className="text-xs text-muted-foreground">Quick ID check for secure bookings. Your info is protected under the Data Privacy Act.</p>
                    <div><Label>Birthday</Label><Input data-testid="player-birthday-input" type="date" value={playerKycForm.birthday} onChange={e => { const value = e.target.value; setPlayerKycForm({...playerKycForm, birthday: value}); setPlayerAgeError(validateAdultBirthday(value)); }} disabled={playerKyc?.identity_review_status === "pending"} />{playerAgeError && <p className="text-xs text-red-600 mt-1" data-testid="player-age-error">{playerAgeError}</p>}</div>
                    <div><Label>Government ID Type</Label>
                      <Select value={playerKycForm.gov_id_type} onValueChange={v => setPlayerKycForm({...playerKycForm, gov_id_type: v})} disabled={playerKyc?.identity_review_status === "pending"}>
                        <SelectTrigger data-testid="player-gov-id"><SelectValue placeholder="Select ID type..." /></SelectTrigger>
                        <SelectContent>{(playerKyc.accepted_ids || []).map(id => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div>
                      <Label>Upload Government ID Photo *</Label>
                      <div className="mt-2">
                        {playerKyc?.gov_id_url ? (
                          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200" data-testid="player-gov-id-uploaded-card">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-green-800">ID Document Uploaded</p>
                              <p className="text-xs text-green-600 truncate">{playerKyc.gov_id_type || "Government ID"}</p>
                            </div>
                            <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(playerKyc.gov_id_url, '_blank')} data-testid="player-gov-id-view-button">
                              <Eye className="w-3 h-3 mr-1" />View
                            </Button>
                            <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200" onClick={() => deletePlayerDoc('government_id')} data-testid="player-gov-id-delete-button">
                              Delete
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.pdf"
                              onChange={e => setPlayerGovIdFile(e.target.files?.[0] || null)}
                              className="hidden"
                              id="player-gov-id-upload"
                              data-testid="player-gov-id-upload"
                            />
                            <label htmlFor="player-gov-id-upload" className="cursor-pointer block">
                              <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                              <p className="text-sm font-medium">{playerGovIdFile ? playerGovIdFile.name : "Click to upload ID photo"}</p>
                              <p className="text-xs text-muted-foreground">JPEG or PDF (max 5MB)</p>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Upload Selfie Photo *</Label>
                      <p className="text-xs text-muted-foreground mb-2">Take a clear selfie holding your ID for verification</p>
                      <div className="mt-1">
                        {playerKyc?.selfie_url ? (
                          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200" data-testid="player-selfie-uploaded-card">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-green-800">Selfie Uploaded</p>
                              <p className="text-xs text-green-600">Verification photo submitted</p>
                            </div>
                            <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(playerKyc.selfie_url, '_blank')} data-testid="player-selfie-view-button">
                              <Eye className="w-3 h-3 mr-1" />View
                            </Button>
                            <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200" onClick={() => deletePlayerDoc('selfie')} data-testid="player-selfie-delete-button">
                              Delete
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                            <input
                              type="file"
                              accept=".jpg,.jpeg"
                              onChange={e => setPlayerSelfieFile(e.target.files?.[0] || null)}
                              className="hidden"
                              id="player-selfie-upload"
                              data-testid="player-selfie-upload"
                            />
                            <label htmlFor="player-selfie-upload" className="cursor-pointer block">
                              <User className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                              <p className="text-sm font-medium">{playerSelfieFile ? playerSelfieFile.name : "Click to upload selfie"}</p>
                              <p className="text-xs text-muted-foreground">Clear photo of you holding your ID</p>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                    {playerKyc?.identity_review_status && playerKyc.identity_review_status !== "incomplete" && (
                      <div className={`p-3 rounded-lg border ${
                        playerKyc.identity_review_status === "approved" ? "bg-green-50 border-green-200" :
                        playerKyc.identity_review_status === "rejected" ? "bg-red-50 border-red-200" :
                        playerKyc.identity_review_status === "retracted" ? "bg-slate-50 border-slate-200" :
                        "bg-amber-50 border-amber-200"
                      }`} data-testid="player-identity-review-status">
                        <p className={`text-sm font-medium ${
                          playerKyc.identity_review_status === "approved" ? "text-green-800" :
                          playerKyc.identity_review_status === "rejected" ? "text-red-800" :
                          playerKyc.identity_review_status === "retracted" ? "text-slate-700" :
                          "text-amber-800"
                        }`}>
                          {playerKyc.identity_review_status === "approved" && "Identity Verified"}
                          {playerKyc.identity_review_status === "rejected" && "Identity Rejected"}
                          {playerKyc.identity_review_status === "pending" && "Identity Under Review — Form is read-only"}
                          {playerKyc.identity_review_status === "retracted" && "Submission Retracted — Edit and resubmit"}
                        </p>
                        {playerKyc.identity_review_notes && <p className="text-xs mt-1">{playerKyc.identity_review_notes}</p>}
                      </div>
                    )}
                    {(playerKyc?.identity_review_status === 'rejected' || playerKyc?.identity_review_status === 'more_info_requested') && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="player-resubmission-checklist">
                        <p className="text-sm font-medium text-red-800 mb-2">Resubmission Checklist</p>
                        <ul className="list-disc pl-5 text-xs text-red-700 space-y-1">
                          {playerChecklist.map((item, idx) => <li key={idx}>{item}</li>)}
                        </ul>
                      </div>
                    )}
                    <SubmissionStatusTimeline
                      currentStatus={playerKyc?.identity_review_status === 'approved' ? 'approved' : playerKyc?.identity_review_status === 'rejected' ? 'rejected' : playerKyc?.identity_review_status === 'more_info_requested' ? 'more_info_requested' : playerKyc?.identity_review_status === 'pending' ? 'under_review' : playerKyc?.identity_review_status === 'retracted' ? 'submitted' : 'submitted'}
                      submittedAt={playerKyc?.identity_submitted_at}
                      reviewNotes={playerKyc?.identity_review_notes}
                      testId="player-verification-timeline"
                    />
                    {playerKyc?.identity_review_status === "pending" ? (
                      <div className="space-y-2">
                        <Button onClick={handleRetractPlayerKyc} disabled={playerRetracting || (() => { if (!playerKyc?.identity_submitted_at) return true; const elapsed = (Date.now() - new Date(playerKyc.identity_submitted_at).getTime()) / 1000; return elapsed < 1800; })()} variant="outline" className="w-full rounded-full border-2" size="sm" data-testid="retract-kyc-btn">
                          {playerRetracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}Retract and Edit Submission
                        </Button>
                        {playerKyc?.identity_submitted_at && (Date.now() - new Date(playerKyc.identity_submitted_at).getTime()) / 1000 < 1800 && (
                          <p className="text-xs text-center text-muted-foreground" data-testid="kyc-retract-cooldown">
                            You can retract after 30 minutes. {Math.max(0, Math.ceil((1800 - (Date.now() - new Date(playerKyc.identity_submitted_at).getTime()) / 1000) / 60))} minute(s) remaining.
                          </p>
                        )}
                      </div>
                    ) : (
                      <Button onClick={handleSubmitPlayerVerification} disabled={!playerKycForm.gov_id_type || !playerKycForm.birthday || !!playerAgeError || playerUploading || (!playerGovIdFile && !playerKyc?.gov_id_url) || (!playerSelfieFile && !playerKyc?.selfie_url)} className="w-full rounded-full" size="sm" data-testid="player-verify-account-button">
                        {playerUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}Submit Identity Documents
                      </Button>
                    )}
                  </>
                )}

                {/* Level 2 confirmed */}
                {playerKyc.kyc_level >= 2 && playerKyc.gov_id_type && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm">Verified with {playerKyc.gov_id_type}</span>
                  </div>
                )}

                {/* Level 3 Progress */}
                {playerKyc.kyc_level >= 2 && playerKyc.kyc_level < 3 && (
                  <>
                    <Separator />
                    <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Path to Trusted Player</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1"><span className={playerKyc.completed_bookings >= 10 ? "text-green-600" : "text-muted-foreground"}>{playerKyc.completed_bookings}/10 bookings</span></div>
                      <div className="flex items-center gap-1"><span className={playerKyc.open_disputes === 0 ? "text-green-600" : "text-red-500"}>{playerKyc.open_disputes} disputes</span></div>
                    </div>
                  </>
                )}

                {/* Trusted Player Extra Info */}
                {playerKyc.kyc_level >= 2 && (
                  <>
                    <Separator />
                    <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Additional Info (Optional)</p>
                    <div><Label>Emergency Contact</Label><Input value={playerKycForm.emergency_contact} onChange={e => setPlayerKycForm({...playerKycForm, emergency_contact: e.target.value})} placeholder="Name and phone number" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Facebook</Label><Input value={playerKycForm.social_facebook} onChange={e => setPlayerKycForm({...playerKycForm, social_facebook: e.target.value})} placeholder="@handle" /></div>
                      <div><Label>Instagram</Label><Input value={playerKycForm.social_instagram} onChange={e => setPlayerKycForm({...playerKycForm, social_instagram: e.target.value})} placeholder="@handle" /></div>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-full" onClick={async () => {
                      try {
                        await api.post("/player/kyc/trusted-info", playerKycForm);
                        toast.success("Info updated!"); const res = await api.get("/player/kyc-status"); setPlayerKyc(res.data);
                      } catch { toast.error("Failed"); }
                    }}><Save className="w-3 h-3 mr-1" />Save Additional Info</Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          <Button onClick={handleSavePersonal} disabled={saving} className="w-full rounded-full" size="lg"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Profile"}</Button>
        </TabsContent>

        {/* ═══ VENUE OWNER TAB (Provider only) ═══ */}
        {isProvider && (
          <TabsContent value="venue-owner" className="space-y-4 mt-4">
            {/* KYC Status Card */}
            <Card className="border-primary/20">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Shield className="w-5 h-5 text-primary" />Verification Status</h3>
                  <Badge className={kyc?.kyc_level >= 3 ? "bg-green-100 text-green-800" : kyc?.kyc_level >= 1 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-800"}>
                    Level {kyc?.kyc_level || 0}: {kyc?.levels?.[kyc?.kyc_level || 0]?.label || "Registered"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    {key: "registration", label: "Account", step: 0},
                    {key: "identity", label: "Identity", step: 1},
                    {key: "ownership", label: "Venue", step: 2},
                    {key: "payment", label: "Payment", step: 3},
                  ].map(s => {
                    const done = kyc?.steps?.[s.key] || (kyc?.kyc_level || 0) > s.step;
                    return (
                      <div key={s.key} className={`text-center p-2 rounded-lg border ${done ? "bg-green-50 border-green-200" : "border-dashed border-slate-200"}`}>
                        <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${done ? "text-green-500" : "text-slate-200"}`} />
                        <p className="text-[10px] font-medium">{s.label}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Role & Identity */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><User className="w-5 h-5" />Venue Owner Role & Identity</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label>Your Role</Label>
                  <Select value={kycForm.venue_role} onValueChange={v => setKycForm({...kycForm, venue_role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Venue Owner">Venue Owner</SelectItem>
                      <SelectItem value="Venue Manager">Venue Manager</SelectItem>
                      <SelectItem value="Authorized Representative">Authorized Representative</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label>Government-Issued ID Type *</Label>
                  <Select value={kycForm.gov_id_type} onValueChange={v => setKycForm({...kycForm, gov_id_type: v})}>
                    <SelectTrigger data-testid="kyc-gov-id-type"><SelectValue placeholder="Select ID type..." /></SelectTrigger>
                    <SelectContent>
                      {(kyc?.accepted_ids || ["Passport","Driver's License","PhilSys National ID","UMID","PRC ID","Postal ID","Voter's ID"]).map(id => (
                        <SelectItem key={id} value={id}>{id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select></div>
                
                {/* Government ID Upload */}
                <div>
                  <Label>Upload Government ID Photo *</Label>
                  <div className="mt-2">
                    {kyc?.gov_id_url ? (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-green-800">ID Document Uploaded</p>
                          <p className="text-xs text-green-600 truncate">{kyc.gov_id_type || "Government ID"}</p>
                        </div>
                        <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(kyc.gov_id_url, '_blank')}>
                          <Eye className="w-3 h-3 mr-1" />View
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.pdf"
                          onChange={e => setGovIdFile(e.target.files?.[0] || null)}
                          className="hidden"
                          id="gov-id-upload"
                          data-testid="kyc-gov-id-upload"
                        />
                        <label htmlFor="gov-id-upload" className="cursor-pointer">
                          <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm font-medium">{govIdFile ? govIdFile.name : "Click to upload ID photo"}</p>
                          <p className="text-xs text-muted-foreground">JPEG or PDF (max 5MB)</p>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Selfie Upload */}
                <div>
                  <Label>Upload Selfie Photo *</Label>
                  <p className="text-xs text-muted-foreground mb-2">Take a clear selfie holding your ID for verification</p>
                  <div className="mt-1">
                    {kyc?.selfie_url ? (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-green-800">Selfie Uploaded</p>
                          <p className="text-xs text-green-600">Verification photo submitted</p>
                        </div>
                        <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(kyc.selfie_url, '_blank')}>
                          <Eye className="w-3 h-3 mr-1" />View
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                        <input
                          type="file"
                          accept=".jpg,.jpeg"
                          onChange={e => setSelfieFile(e.target.files?.[0] || null)}
                          className="hidden"
                          id="selfie-upload"
                          data-testid="kyc-selfie-upload"
                        />
                        <label htmlFor="selfie-upload" className="cursor-pointer">
                          <User className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm font-medium">{selfieFile ? selfieFile.name : "Click to upload selfie"}</p>
                          <p className="text-xs text-muted-foreground">Clear photo of you holding your ID</p>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                
              </CardContent>
            </Card>

            {/* Payout Details */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><CreditCard className="w-5 h-5" />Payout / Bank Details</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><Label>Payout Method *</Label>
                  <Select value={kycForm.payout_method} onValueChange={v => setKycForm({...kycForm, payout_method: v})}>
                    <SelectTrigger data-testid="kyc-payout-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_account">Bank Account</SelectItem>
                      <SelectItem value="gcash">GCash</SelectItem>
                      <SelectItem value="maya">Maya (PayMaya)</SelectItem>
                    </SelectContent>
                  </Select></div>

                {kycForm.payout_method === "bank_account" && (
                  <div className="space-y-3">
                    <div><Label>Bank Name</Label><Input value={kycForm.bank_name} onChange={e => setKycForm({...kycForm, bank_name: e.target.value})} placeholder="BDO, BPI, Metrobank, etc." /></div>
                    <div><Label>Account Holder Name</Label><Input value={kycForm.account_name} onChange={e => setKycForm({...kycForm, account_name: e.target.value})} placeholder="Must match verified identity" /></div>
                    <div><Label>Account Number</Label><Input value={kycForm.account_number} onChange={e => setKycForm({...kycForm, account_number: e.target.value})} placeholder="Enter bank account number" /></div>
                  </div>
                )}
                {kycForm.payout_method === "gcash" && (
                  <div><Label>GCash Number</Label><Input value={kycForm.gcash_number} onChange={e => setKycForm({...kycForm, gcash_number: e.target.value})} placeholder="+63 9XX XXX XXXX" /></div>
                )}
                {kycForm.payout_method === "maya" && (
                  <div><Label>Maya Number</Label><Input value={kycForm.maya_number} onChange={e => setKycForm({...kycForm, maya_number: e.target.value})} placeholder="+63 9XX XXX XXXX" /></div>
                )}
                <p className="text-[10px] text-muted-foreground">Account holder name must match your verified identity or registered business name. Changes to payout details require re-verification.</p>
              </CardContent>
            </Card>

            {/* Save / Status Button */}
            {kyc?.identity_review_status === "pending" ? (
              <>
                <Button disabled className="w-full rounded-full bg-court-orange hover:bg-court-orange cursor-not-allowed opacity-90" size="lg" data-testid="check-email-btn">
                  <Mail className="w-4 h-4 mr-2" />Check your Email for Approval Notification
                </Button>
                {/* Identity Under Review — moved below the button */}
                <div className="p-3 rounded-lg border bg-amber-50 border-amber-200">
                  <p className="text-sm font-medium text-amber-800">Identity Under Review</p>
                  {kyc.identity_review_notes && <p className="text-xs mt-1 text-amber-700">{kyc.identity_review_notes}</p>}
                </div>
              </>
            ) : kyc?.identity_review_status === "approved" ? (
              <>
                <Button onClick={() => setEditIdentityWarning(true)} className="w-full rounded-full bg-court-green hover:bg-court-green-hover text-white" size="lg" data-testid="edit-venue-owner-btn">
                  <Save className="w-4 h-4 mr-2" />Edit / Save Venue Owner Profile
                </Button>
                {/* Identity Verified */}
                <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                  <p className="text-sm font-medium text-green-800">Identity Verified</p>
                  {kyc.identity_review_notes && <p className="text-xs mt-1 text-green-700">{kyc.identity_review_notes}</p>}
                </div>
              </>
            ) : (
              <>
                <Button onClick={handleSaveVenueOwner} disabled={saving || uploading} className="w-full rounded-full" size="lg" data-testid="save-venue-owner-btn">
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading Documents...</>
                  ) : saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save Venue Owner Profile</>
                  )}
                </Button>
                {/* Show rejected status if applicable */}
                {kyc?.identity_review_status === "rejected" && (
                  <div className="p-3 rounded-lg border bg-red-50 border-red-200">
                    <p className="text-sm font-medium text-red-800">Identity Rejected</p>
                    {kyc.identity_review_notes && <p className="text-xs mt-1 text-red-700">{kyc.identity_review_notes}</p>}
                  </div>
                )}
              </>
            )}

            {/* Continue to Create Venue - shown ONLY after identity is APPROVED */}
            {kyc?.identity_review_status === "approved" && (
              <Card className="border-primary bg-primary/5">
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-bold text-sm">Next: Create Your Venue</h4>
                      <p className="text-xs text-muted-foreground">Set up your venue details, upload ownership documents (business permits, photos), and submit for verification.</p>
                    </div>
                    <Button 
                      onClick={() => navigate("/provider/new-venue")}
                      className="bg-court-green hover:bg-court-green/90 text-white rounded-full"
                      data-testid="continue-to-venue-btn"
                    >
                      Create Venue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Edit Identity Warning Dialog */}
            <Dialog open={editIdentityWarning} onOpenChange={setEditIdentityWarning}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase">Edit Approved Identity</DialogTitle>
                  <DialogDescription className="sr-only">Warning about editing approved identity</DialogDescription>
                </DialogHeader>
                <p className="text-sm text-slate-600 py-2">Changing your approved identity will be subjected for another review.</p>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setEditIdentityWarning(false)}>Cancel</Button>
                  <Button onClick={() => { setEditIdentityWarning(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="bg-court-green hover:bg-court-green-hover text-white">Ok</Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* ═══ PROFESSIONAL TAB (SP only) ═══ */}
        {isSP && (
          <TabsContent value="professional" className="space-y-4 mt-4">
            {/* SP KYC Verification Progress */}
            {spKyc && (
              <Card className="border-primary/20">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Shield className="w-5 h-5 text-primary" />Verification Status</h3>
                    <Badge className={spKyc.kyc_level >= 4 ? "bg-green-100 text-green-800" : spKyc.kyc_level >= 2 ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}>
                      Level {spKyc.kyc_level}: {spKyc.levels?.[spKyc.kyc_level]?.label || "Registered"}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[{lvl:1,label:"Registered"},{lvl:2,label:"ID Verified"},{lvl:3,label:"Credentials"},{lvl:4,label:"Payment"}].map(s => (
                      <div key={s.lvl} className={`text-center p-2 rounded-lg border ${spKyc.kyc_level >= s.lvl ? "bg-green-50 border-green-200" : "border-dashed"}`}>
                        <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${spKyc.kyc_level >= s.lvl ? "text-green-500" : "text-slate-200"}`} />
                        <p className="text-[10px] font-medium">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Level 2: Identity Verification with Document Upload */}
                  {spKyc.kyc_level < 2 && (
                    <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                      <p className="text-sm font-medium text-blue-800">Verify your identity to become visible to players</p>
                      
                      {/* Government ID Type */}
                      <div>
                        <Label className="text-xs font-heading uppercase">Government ID Type *</Label>
                        <Select value={spKycForm.gov_id_type} onValueChange={v => setSpKycForm({...spKycForm, gov_id_type: v})}>
                          <SelectTrigger><SelectValue placeholder="Select ID type..." /></SelectTrigger>
                          <SelectContent>{["Passport","Driver's License","PhilSys National ID","UMID","PRC ID","Postal ID","Voter's ID"].map(id => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      
                      {/* Government ID Upload */}
                      <div>
                        <Label className="text-xs font-heading uppercase">Upload Government ID Photo *</Label>
                        <div className="mt-2">
                          {spKyc?.gov_id_url ? (
                            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-green-800">ID Document Uploaded</p>
                              </div>
                              <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(spKyc.gov_id_url, '_blank')}>
                                <Eye className="w-3 h-3 mr-1" />View
                              </Button>
                              <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200" onClick={() => deleteSpDoc('government_id')}>
                                Delete
                              </Button>
                            </div>
                          ) : (
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.pdf"
                                onChange={e => setSpGovIdFile(e.target.files?.[0] || null)}
                                className="hidden"
                                id="sp-gov-id-upload"
                              />
                              <label htmlFor="sp-gov-id-upload" className="cursor-pointer">
                                <Upload className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                                <p className="text-sm font-medium">{spGovIdFile ? spGovIdFile.name : "Click to upload ID photo"}</p>
                                <p className="text-xs text-muted-foreground">JPEG or PDF (max 5MB)</p>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Selfie Upload */}
                      <div>
                        <Label className="text-xs font-heading uppercase">Upload Selfie Photo *</Label>
                        <p className="text-xs text-muted-foreground mb-2">Clear photo of you holding your ID</p>
                        <div className="mt-1">
                          {spKyc?.selfie_url ? (
                            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-green-800">Selfie Uploaded</p>
                              </div>
                              <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(spKyc.selfie_url, '_blank')}>
                                <Eye className="w-3 h-3 mr-1" />View
                              </Button>
                              <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200" onClick={() => deleteSpDoc('selfie')}>
                                Delete
                              </Button>
                            </div>
                          ) : (
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                              <input
                                type="file"
                                accept=".jpg,.jpeg"
                                onChange={e => setSpSelfieFile(e.target.files?.[0] || null)}
                                className="hidden"
                                id="sp-selfie-upload"
                              />
                              <label htmlFor="sp-selfie-upload" className="cursor-pointer">
                                <User className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                                <p className="text-sm font-medium">{spSelfieFile ? spSelfieFile.name : "Click to upload selfie"}</p>
                                <p className="text-xs text-muted-foreground">Clear photo of you holding your ID</p>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Identity Review Status */}
                      {spKyc?.identity_review_status && spKyc.identity_review_status !== "incomplete" && (
                        <div className={`p-3 rounded-lg border ${
                          spKyc.identity_review_status === "approved" ? "bg-green-50 border-green-200" :
                          spKyc.identity_review_status === "rejected" ? "bg-red-50 border-red-200" :
                          "bg-amber-50 border-amber-200"
                        }`}>
                          <p className={`text-sm font-medium ${
                            spKyc.identity_review_status === "approved" ? "text-green-800" :
                            spKyc.identity_review_status === "rejected" ? "text-red-800" :
                            "text-amber-800"
                          }`}>
                            {spKyc.identity_review_status === "approved" && "✓ Identity Verified"}
                            {spKyc.identity_review_status === "rejected" && "✗ Identity Rejected"}
                            {spKyc.identity_review_status === "pending" && "⏳ Identity Under Review"}
                          </p>
                          {spKyc.identity_review_notes && (
                            <p className="text-xs mt-1">{spKyc.identity_review_notes}</p>
                          )}
                        </div>
                      )}
                      {(spKyc?.identity_review_status === 'rejected' || spKyc?.identity_review_status === 'more_info_requested') && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="sp-identity-resubmission-checklist">
                          <p className="text-sm font-medium text-red-800 mb-2">Identity Resubmission Checklist</p>
                          <ul className="list-disc pl-5 text-xs text-red-700 space-y-1">
                            {spChecklist.map((item, idx) => <li key={idx}>{item}</li>)}
                          </ul>
                        </div>
                      )}
                      <SubmissionStatusTimeline
                        currentStatus={spKyc?.identity_review_status === 'approved' ? 'approved' : spKyc?.identity_review_status === 'rejected' ? 'rejected' : spKyc?.identity_review_status === 'more_info_requested' ? 'more_info_requested' : spKyc?.identity_review_status === 'pending' ? 'under_review' : 'submitted'}
                        submittedAt={spKyc?.identity_submitted_at}
                        reviewNotes={spKyc?.identity_review_notes}
                        testId="sp-identity-timeline"
                      />
                      
                      <Button size="sm" className="rounded-full w-full" 
                        disabled={!spKycForm.gov_id_type || spUploading || (!spGovIdFile && !spKyc?.gov_id_url) || (!spSelfieFile && !spKyc?.selfie_url)} 
                        onClick={async () => {
                          setSpUploading(true);
                          try {
                            let govIdUrl = spKyc?.gov_id_url || "";
                            let selfieUrl = spKyc?.selfie_url || "";
                            
                            // Upload Government ID
                            if (spGovIdFile) {
                              const formData = new FormData();
                              formData.append("file", spGovIdFile);
                              formData.append("doc_type", "government_id");
                              formData.append("category", "sp_kyc_identity");
                              const uploadRes = await api.post("/uploads/kyc-document", formData, {
                                headers: { "Content-Type": "multipart/form-data" }
                              });
                              govIdUrl = uploadRes.data.url;
                            }
                            
                            // Upload Selfie
                            if (spSelfieFile) {
                              const formData = new FormData();
                              formData.append("file", spSelfieFile);
                              formData.append("doc_type", "selfie");
                              formData.append("category", "sp_kyc_identity");
                              const uploadRes = await api.post("/uploads/kyc-document", formData, {
                                headers: { "Content-Type": "multipart/form-data" }
                              });
                              selfieUrl = uploadRes.data.url;
                            }
                            
                            // Submit identity verification
                            await api.post("/sp/kyc/identity", { 
                              gov_id_type: spKycForm.gov_id_type,
                              gov_id_url: govIdUrl,
                              selfie_url: selfieUrl,
                            }); 
                            toast.success("Identity documents submitted for review!"); 
                            setSpIdentitySubmitted(true);
                            const r = await api.get("/sp/kyc-status"); 
                            setSpKyc(r.data); 
                          } catch (e) { 
                            toast.error(e.response?.data?.detail || "Upload failed"); 
                          } finally {
                            setSpUploading(false);
                          }
                        }}>
                        {spUploading ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Uploading...</>
                        ) : (
                          <><CheckCircle className="w-3 h-3 mr-1" />Submit Identity Documents</>
                        )}
                      </Button>
                    </div>
                  )}
                  {spKyc.kyc_level >= 2 && spKyc.gov_id_type && <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200"><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm">ID Verified: {spKyc.gov_id_type}</span></div>}

                  {/* Level 3: Professional Credentials with Document Uploads */}
                  {spKyc.kyc_level >= 2 && spKyc.kyc_level < 3 && (
                    <div className="bg-purple-50 rounded-lg p-4 space-y-4">
                      <p className="text-sm font-medium text-purple-800">Upload your professional credentials to accept bookings</p>
                      <p className="text-xs text-muted-foreground">Upload certificates, licenses, or membership documents that prove your professional qualifications.</p>
                      
                      {/* Credential Type Selection */}
                      <div>
                        <Label className="text-xs font-heading uppercase">Credential Type *</Label>
                        <Select value={spKycForm.credential_type} onValueChange={v => setSpKycForm({...spKycForm, credential_type: v})}>
                          <SelectTrigger><SelectValue placeholder="Select credential type..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="coaching_certificate">Coaching Certificate</SelectItem>
                            <SelectItem value="prc_license">PRC License</SelectItem>
                            <SelectItem value="ncaa_certification">NCAA/Sports Certification</SelectItem>
                            <SelectItem value="association_membership">Sports Association Membership</SelectItem>
                            <SelectItem value="training_certification">Training/Fitness Certification</SelectItem>
                            <SelectItem value="officiating_license">Officiating License</SelectItem>
                            <SelectItem value="other_credential">Other Professional Credential</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Professional Credentials Upload */}
                      <div>
                        <Label className="text-xs font-heading uppercase">Upload Credential Documents *</Label>
                        <p className="text-xs text-muted-foreground mb-2">Upload up to 5 documents (certificates, licenses, membership cards)</p>
                        
                        {/* Uploaded Credentials List */}
                        {spKyc?.credential_urls?.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {spKyc.credential_urls.map((url, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-2 bg-green-50 rounded-lg border border-green-200">
                                <FileText className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm flex-1">Document {idx + 1}</span>
                                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => window.open(url, '_blank')}>
                                  <Eye className="w-3 h-3 mr-1" />View
                                </Button>
                                <Button variant="outline" size="sm" className="text-xs h-7 text-red-600 border-red-200" onClick={() => deleteSpDoc('professional_credential')}>
                                  Delete
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* New Upload Area */}
                        <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.pdf"
                            multiple
                            onChange={e => {
                              const files = Array.from(e.target.files || []).slice(0, 5 - (spKyc?.credential_urls?.length || 0));
                              setSpCredentialFiles(files);
                            }}
                            className="hidden"
                            id="sp-credential-upload"
                          />
                          <label htmlFor="sp-credential-upload" className="cursor-pointer">
                            <Award className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                            <p className="text-sm font-medium">
                              {spCredentialFiles.length > 0 ? `${spCredentialFiles.length} file(s) selected` : "Click to upload credentials"}
                            </p>
                            <p className="text-xs text-muted-foreground">JPEG or PDF (max 5MB each)</p>
                          </label>
                        </div>
                      </div>
                      
                      {/* Character References (Optional) */}
                      <div className="pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Optional: Provide character references for additional verification</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={spKycForm.credential_ref1} onChange={e => setSpKycForm({...spKycForm, credential_ref1: e.target.value})} placeholder="Reference 1 (name & contact)" className="text-sm" />
                          <Input value={spKycForm.credential_ref2} onChange={e => setSpKycForm({...spKycForm, credential_ref2: e.target.value})} placeholder="Reference 2 (name & contact)" className="text-sm" />
                        </div>
                      </div>
                      
                      {/* Credentials Review Status */}
                      {spKyc?.credentials_review_status && spKyc.credentials_review_status !== "incomplete" && (
                        <div className={`p-3 rounded-lg border ${
                          spKyc.credentials_review_status === "approved" ? "bg-green-50 border-green-200" :
                          spKyc.credentials_review_status === "rejected" ? "bg-red-50 border-red-200" :
                          "bg-amber-50 border-amber-200"
                        }`}>
                          <p className={`text-sm font-medium ${
                            spKyc.credentials_review_status === "approved" ? "text-green-800" :
                            spKyc.credentials_review_status === "rejected" ? "text-red-800" :
                            "text-amber-800"
                          }`}>
                            {spKyc.credentials_review_status === "approved" && "✓ Credentials Approved"}
                            {spKyc.credentials_review_status === "rejected" && "✗ Credentials Rejected"}
                            {spKyc.credentials_review_status === "pending" && "⏳ Credentials Under Review"}
                          </p>
                          {spKyc.credentials_review_notes && (
                            <p className="text-xs mt-1">{spKyc.credentials_review_notes}</p>
                          )}
                        </div>
                      )}
                      {(spKyc?.credentials_review_status === 'rejected' || spKyc?.credentials_review_status === 'more_info_requested') && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="sp-credential-resubmission-checklist">
                          <p className="text-sm font-medium text-red-800 mb-2">Credential Resubmission Checklist</p>
                          <ul className="list-disc pl-5 text-xs text-red-700 space-y-1">
                            {spChecklist.map((item, idx) => <li key={idx}>{item}</li>)}
                          </ul>
                        </div>
                      )}
                      <SubmissionStatusTimeline
                        currentStatus={spKyc?.credentials_review_status === 'approved' ? 'approved' : spKyc?.credentials_review_status === 'rejected' ? 'rejected' : spKyc?.credentials_review_status === 'more_info_requested' ? 'more_info_requested' : spKyc?.credentials_review_status === 'pending' ? 'under_review' : 'submitted'}
                        submittedAt={spKyc?.credentials_submitted_at}
                        reviewNotes={spKyc?.credentials_review_notes}
                        testId="sp-credential-timeline"
                      />
                      
                      <Button size="sm" className="rounded-full w-full" 
                        disabled={!spKycForm.credential_type || spUploading || (spCredentialFiles.length === 0 && !(spKyc?.credential_urls?.length > 0))} 
                        onClick={async () => {
                          setSpUploading(true);
                          try {
                            let credentialUrls = spKyc?.credential_urls || [];
                            
                            // Upload new credential documents
                            for (const file of spCredentialFiles) {
                              const formData = new FormData();
                              formData.append("file", file);
                              formData.append("doc_type", "professional_credential");
                              formData.append("category", "sp_kyc_credentials");
                              const uploadRes = await api.post("/uploads/kyc-document", formData, {
                                headers: { "Content-Type": "multipart/form-data" }
                              });
                              credentialUrls.push(uploadRes.data.url);
                            }
                            
                            // Submit credentials
                            await api.post("/sp/kyc/credentials", { 
                              credential_type: spKycForm.credential_type,
                              credential_urls: credentialUrls,
                              character_refs: [spKycForm.credential_ref1, spKycForm.credential_ref2].filter(Boolean) 
                            }); 
                            toast.success("Professional credentials submitted for review!"); 
                            setSpCredentialFiles([]);
                            const r = await api.get("/sp/kyc-status"); 
                            setSpKyc(r.data); 
                          } catch (e) { 
                            toast.error(e.response?.data?.detail || "Upload failed"); 
                          } finally {
                            setSpUploading(false);
                          }
                        }}>
                        {spUploading ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Uploading...</>
                        ) : (
                          <><CheckCircle className="w-3 h-3 mr-1" />Submit Professional Credentials</>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Level 4: Payment */}
                  {spKyc.kyc_level >= 3 && spKyc.kyc_level < 4 && (
                    <div className="bg-green-50 rounded-lg p-4 space-y-3">
                      <p className="text-sm font-medium text-green-800">Set up your payout method to receive payments</p>
                      <div><Label className="text-xs">Payout Method</Label>
                        <Select value={spKycForm.payout_method} onValueChange={v => setSpKycForm({...spKycForm, payout_method: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="gcash">GCash</SelectItem><SelectItem value="maya">Maya</SelectItem><SelectItem value="bank_account">Bank Account</SelectItem></SelectContent>
                        </Select></div>
                      {spKycForm.payout_method === "gcash" && <div><Label className="text-xs">GCash Number</Label><Input value={spKycForm.gcash_number} onChange={e => setSpKycForm({...spKycForm, gcash_number: e.target.value})} placeholder="+63 9XX" /></div>}
                      {spKycForm.payout_method === "maya" && <div><Label className="text-xs">Maya Number</Label><Input value={spKycForm.maya_number} onChange={e => setSpKycForm({...spKycForm, maya_number: e.target.value})} placeholder="+63 9XX" /></div>}
                      {spKycForm.payout_method === "bank_account" && <div className="grid grid-cols-1 sm:grid-cols-3 gap-2"><Input value={spKycForm.bank_name} onChange={e => setSpKycForm({...spKycForm, bank_name: e.target.value})} placeholder="Bank" className="text-sm" /><Input value={spKycForm.account_name} onChange={e => setSpKycForm({...spKycForm, account_name: e.target.value})} placeholder="Account name" className="text-sm" /><Input value={spKycForm.account_number} onChange={e => setSpKycForm({...spKycForm, account_number: e.target.value})} placeholder="Account #" className="text-sm" /></div>}
                      <Button size="sm" className="rounded-full" onClick={async () => {
                        try { await api.post("/sp/kyc/payment", spKycForm); toast.success("Payment verified! You can receive payouts."); const r = await api.get("/sp/kyc-status"); setSpKyc(r.data); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }}><CheckCircle className="w-3 h-3 mr-1" />Verify Payment</Button>
                    </div>
                  )}
                  {spKyc.kyc_level >= 4 && <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200"><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm">Fully verified — Payout via {spKyc.payout_method?.replace("_"," ")}</span></div>}

                  <p className="text-[10px] text-muted-foreground">Your information is collected only for identity verification, payment security, and fraud prevention in accordance with the Philippine Data Privacy Act (RA 10173).</p>
                </CardContent>
              </Card>
            )}

            {/* Bio & Experience */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Briefcase className="w-5 h-5" /> Professional Profile</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><Label>Professional Bio</Label><Textarea value={spForm.bio} onChange={e => setSpForm({...spForm, bio: e.target.value})} placeholder="Describe your coaching style, experience, achievements..." className="text-sm" rows={4} data-testid="sp-bio" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Years of Experience</Label><Input type="number" value={spForm.years_experience} onChange={e => setSpForm({...spForm, years_experience: parseInt(e.target.value)||0})} /></div>
                  <div><Label>Governing Body / Association</Label><Input value={spForm.governing_body} onChange={e => setSpForm({...spForm, governing_body: e.target.value})} placeholder="e.g. PSC, PBA, FIBA" /></div>
                </div>
                <div><Label>Service Locations (comma-separated)</Label><Input value={spForm.service_locations} onChange={e => setSpForm({...spForm, service_locations: e.target.value})} placeholder="Makati, Taguig, Pasig" /></div>
                <div className="flex items-center gap-3"><Switch checked={spForm.insurance_declared} onCheckedChange={v => setSpForm({...spForm, insurance_declared: v})} /><Label>I have professional liability insurance</Label></div>
              </CardContent>
            </Card>

            {/* Role-Specific: Coach */}
            {spProfile?.roles_enabled?.includes("coach") && (
              <Card>
                <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Award className="w-5 h-5 text-blue-600" /> Coach Settings</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Coaching Level</Label>
                      <Select value={spForm.coaching_level} onValueChange={v => setSpForm({...spForm, coaching_level: v})}>
                        <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                        <SelectContent>{COACHING_LEVELS.map(l => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label>Max Group Size</Label><Input type="number" value={spForm.max_group_size} onChange={e => setSpForm({...spForm, max_group_size: parseInt(e.target.value)||15})} /></div>
                  </div>
                  <div><Label>Specializations (comma-separated)</Label><Input value={spForm.specializations} onChange={e => setSpForm({...spForm, specializations: e.target.value})} placeholder="shooting, defense, agility, tactics" /></div>
                </CardContent>
              </Card>
            )}

            {/* Role-Specific: Trainer */}
            {spProfile?.roles_enabled?.includes("trainer") && (
              <Card>
                <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Dumbbell className="w-5 h-5 text-red-600" /> Trainer Settings</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><Label>Training Type</Label>
                    <Select value={spForm.training_type} onValueChange={v => setSpForm({...spForm, training_type: v})}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>{TRAINING_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace("_"," ")}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Equipment Required</Label><Input value={spForm.equipment_required} onChange={e => setSpForm({...spForm, equipment_required: e.target.value})} placeholder="e.g. Resistance bands, dumbbells" /></div>
                  <div className="flex items-center gap-3"><Switch checked={spForm.mobile_service} onCheckedChange={v => setSpForm({...spForm, mobile_service: v})} /><Label>I offer mobile / on-site training</Label></div>
                </CardContent>
              </Card>
            )}

            {/* Role-Specific: Official */}
            {spProfile?.roles_enabled?.includes("official") && (
              <Card>
                <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Shield className="w-5 h-5 text-purple-600" /> Official Settings</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Official Type</Label>
                      <Select value={spForm.official_type} onValueChange={v => setSpForm({...spForm, official_type: v})}>
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>{OFFICIAL_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace("_"," ")}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label>Certification Level</Label><Input value={spForm.certification_level} onChange={e => setSpForm({...spForm, certification_level: e.target.value})} placeholder="e.g. Certified, National" /></div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pricing */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><DollarSign className="w-5 h-5" /> Pricing</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div><Label>Per Hour (₱)</Label><Input type="number" value={spForm.per_hour} onChange={e => setSpForm({...spForm, per_hour: e.target.value})} /></div>
                  <div><Label>Per Game (₱)</Label><Input type="number" value={spForm.per_game} onChange={e => setSpForm({...spForm, per_game: e.target.value})} /></div>
                  <div><Label>Per Session (₱)</Label><Input type="number" value={spForm.per_session} onChange={e => setSpForm({...spForm, per_session: e.target.value})} /></div>
                  <div><Label>Half Day (₱)</Label><Input type="number" value={spForm.half_day} onChange={e => setSpForm({...spForm, half_day: e.target.value})} /></div>
                  <div><Label>Full Day (₱)</Label><Input type="number" value={spForm.full_day} onChange={e => setSpForm({...spForm, full_day: e.target.value})} /></div>
                  <div><Label>Travel Surcharge (₱)</Label><Input type="number" value={spForm.travel_surcharge} onChange={e => setSpForm({...spForm, travel_surcharge: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Series Discount (%)</Label><Input type="number" value={spForm.series_discount_pct} onChange={e => setSpForm({...spForm, series_discount_pct: e.target.value})} /></div>
                  <div><Label>Peak Multiplier</Label><Input type="number" step="0.1" value={spForm.peak_multiplier} onChange={e => setSpForm({...spForm, peak_multiplier: e.target.value})} /></div>
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Star className="w-5 h-5" /> Social & Online Presence</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><Label>Facebook</Label><Input value={spForm.social_facebook} onChange={e => setSpForm({...spForm, social_facebook: e.target.value})} placeholder="facebook.com/yourpage" /></div>
                <div><Label>Instagram</Label><Input value={spForm.social_instagram} onChange={e => setSpForm({...spForm, social_instagram: e.target.value})} placeholder="@yourhandle" /></div>
                <div><Label>TikTok</Label><Input value={spForm.social_tiktok} onChange={e => setSpForm({...spForm, social_tiktok: e.target.value})} placeholder="@yourhandle" /></div>
              </CardContent>
            </Card>

            {/* SP Status */}
            {spProfile && (
              <Card>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Professional Status</p>
                    <div className="flex gap-1 mt-1">{spProfile.roles_enabled?.map(r => <Badge key={r} variant="outline" className="capitalize text-[10px]">{r}</Badge>)}</div>
                  </div>
                  <div className="text-right">
                    <Badge className={spProfile.verification_status === "verified" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>{spProfile.verification_status}</Badge>
                    <p className="text-xs text-muted-foreground mt-1"><Star className="w-3 h-3 inline text-amber-500" /> {spProfile.rating} ({spProfile.total_ratings} reviews)</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button onClick={handleSaveProfessional} disabled={saving} className="w-full rounded-full" size="lg"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Professional Profile"}</Button>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
